/**
 * CalendarHeader — barra header per `PaginaCalendario`. Include:
 *  - Titolo dinamico (Mese / Q · Anno / Settimana N · Anno)
 *  - Pulsanti prev/today/next
 *  - Toggle vista (mese · trimestre · settimana)
 *  - Legenda heatmap (colori cella + scadenza)
 */
import { ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react';
import { MONTH_LABELS_IT, VIEWS } from '../../lib/calendarConstants';

export default function CalendarHeader({
  view, year, month, isoWeek,
  onPrev, onNext, onToday, onViewChange,
}) {
  return (
    <>
      <header className="flex items-end justify-between mb-6 pb-6 border-b border-line" data-testid="cal-header">
        <div>
          <span className="mono-label">Calendario · {view}</span>
          <h1 className="font-display tracking-tight text-4xl md:text-5xl lg:text-6xl mt-2">
            {view === 'mese' && <>{MONTH_LABELS_IT[month - 1]} {year}</>}
            {view === 'trimestre' && <>Q{Math.ceil(month / 3)} · {year}</>}
            {view === 'settimana' && <>Settimana {isoWeek.split('-W')[1]} · {year}</>}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={onPrev}
            className="p-2 border border-line hover:border-ink"
            data-testid="cal-prev"
            aria-label="Precedente"
          >
            <ChevronLeft size={16} strokeWidth={1.5} />
          </button>
          <button
            onClick={onToday}
            className="px-4 py-2 font-mono text-xs uppercase tracking-[0.18em] border border-line hover:border-ink"
            data-testid="cal-today"
          >
            Oggi
          </button>
          <button
            onClick={onNext}
            className="p-2 border border-line hover:border-ink"
            data-testid="cal-next"
            aria-label="Successivo"
          >
            <ChevronRight size={16} strokeWidth={1.5} />
          </button>
        </div>
      </header>

      {/* View toggle */}
      <div className="flex items-center gap-2 mb-5" data-testid="cal-view-toggle">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-mute mr-1">Vista</span>
        {VIEWS.map((v) => {
          const active = view === v;
          return (
            <button
              key={v}
              onClick={() => onViewChange(v)}
              className={
                'font-mono text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 border transition-colors ' +
                (active
                  ? 'border-ink bg-ink text-canvas'
                  : 'border-line text-soft hover:border-ink hover:text-ink')
              }
              data-testid={`cal-view-${v}`}
              aria-pressed={active}
            >
              {v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          );
        })}
      </div>

      {/* Legenda */}
      <div className="mb-4 flex flex-wrap items-center gap-5 font-mono text-[10px] uppercase tracking-[0.15em] text-soft">
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3" style={{ backgroundColor: 'rgb(var(--qp-success-rgb) / 0.20)' }} /> Tutte fatte</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3" style={{ backgroundColor: 'rgba(245,158,11,0.18)' }} /> Parziali</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3" style={{ backgroundColor: 'rgba(229,72,77,0.08)' }} /> Da fare</span>
        <span className="inline-flex items-center gap-1.5"><AlertTriangle size={11} strokeWidth={1.5} className="text-accent" /> Scadenza KR/tappa</span>
      </div>
    </>
  );
}
